'use client';
import { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/components/ThemeProvider';
import { useSettings } from '@/hooks/useSettings';

function SettingsModal({ isOpen, onClose }) {
  const { settings, updateSettings } = useSettings();
  const [draft, setDraft] = useState('');
  const [show, setShow] = useState(false);
  const [saved, setSaved] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (isOpen) {
      setDraft(settings.geminiApiKey || '');
      setSaved(false);
      setShow(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, settings.geminiApiKey]);

  const handleSave = () => {
    updateSettings({ geminiApiKey: draft.trim() });
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      onClose();
    }, 800);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl p-6"
        style={{ background: 'var(--bg-2)', border: '1px solid var(--border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14" />
              </svg>
            </div>
            <div>
              <h2 className="font-semibold text-[15px]" style={{ color: 'var(--text-0)' }}>Configuración</h2>
              <p className="text-xs" style={{ color: 'var(--text-2)' }}>Integraciones de IA</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: 'var(--text-2)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Gemini API Key */}
        <div className="mb-5">
          <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-2)' }}>
            API Key — Nano Banana / Gemini
          </label>
          <div className="relative">
            <input
              ref={inputRef}
              type={show ? 'text' : 'password'}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="AIza... ó nb-..."
              className="w-full rounded-xl px-4 py-2.5 pr-10 text-sm font-mono focus:outline-none transition-all"
              style={{
                background: 'var(--bg-1)',
                border: '1px solid var(--border)',
                color: 'var(--text-0)',
              }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
            />
            <button
              onClick={() => setShow(!show)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--text-2)' }}
            >
              {show ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
          Se guarda localmente en este dispositivo. No se envía al servidor.{' '}
          La API Key es de Google AI Studio —{' '}
          
          <a href="https://aistudio.google.com/apikey"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent)' }}
          >
            conseguila gratis acá
          </a>
          .
        </p>
        </div>

        {/* Status indicator */}
        {settings.geminiApiKey && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4 text-xs"
            style={{ background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid var(--green-border, var(--border))' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            API Key configurada
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={{ border: '1px solid var(--border)', color: 'var(--text-1)', background: 'transparent' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{
              background: saved ? 'var(--green)' : 'var(--accent)',
              color: 'white',
              border: 'none',
            }}
          >
            {saved ? '✓ Guardado' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Header({ breadcrumbs = [], onRefresh }) {
  const { theme, toggle } = useTheme();
  const { settings } = useSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const hasApiKey = !!settings.geminiApiKey;

  return (
    <>
      <header
        className="h-[52px] flex items-center justify-between px-6 sticky top-0 z-40 transition-colors duration-200"
        style={{ background: 'var(--bg-1)', borderBottom: '1px solid var(--border)' }}
      >
        {/* Breadcrumbs */}
        <div className="flex items-center gap-1.5 text-[13px]">
          {breadcrumbs.map((bc, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              )}
              {bc.href ? (
                <a href={bc.href} className="hover:text-[var(--text-0)] transition-colors" style={{ color: 'var(--text-1)' }}>
                  {bc.label}
                </a>
              ) : (
                <span className="font-medium" style={{ color: 'var(--text-0)' }}>{bc.label}</span>
              )}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">

          {/* Settings / Cog */}
          <button
            onClick={() => setSettingsOpen(true)}
            className="w-[34px] h-[34px] rounded-lg flex items-center justify-center transition-colors duration-150 relative"
            style={{
              background: 'var(--bg-elevated)',
              border: `1px solid ${hasApiKey ? 'var(--green)' : 'var(--border)'}`,
              color: hasApiKey ? 'var(--green)' : 'var(--text-1)',
            }}
            title="Configuración"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
            {/* Dot indicator si no hay key */}
            {!hasApiKey && (
              <span
                className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
                style={{ background: 'var(--red, #ef4444)', border: '1.5px solid var(--bg-1)' }}
              />
            )}
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggle}
            className="w-[34px] h-[34px] rounded-lg flex items-center justify-center transition-colors duration-150"
            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
            title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          >
            {theme === 'dark' ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
              </svg>
            )}
          </button>

          {/* Sync button */}
          <button
            onClick={onRefresh}
            className="h-[34px] px-2.5 rounded-lg flex items-center justify-center transition-colors duration-150"
            style={{ background: 'transparent', border: '1px solid transparent', color: 'var(--text-1)' }}
            title="Sincronizar"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
          </button>
        </div>
      </header>

      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}